import { getStepUpPrompt, getStepUpToken, setStepUpToken } from '@/lib/stepup';
import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';

/**
 * 统一 axios 实例。
 *
 * - baseURL 用相对 `/`，由 nginx 单入口按前缀分发到各后端，前端无需关心绝对地址。
 * - 短期 access JWT 保存在当前标签页；旋转 refresh token 保存在 localStorage，
 *   使新标签页和 access token 过期后都能无感续期。
 * - 401 时先尝试账户中心 refresh token 旋转续期，续期失败才清态并跳登录页。
 *
 * 运维前端只调用后台（/admin-api）与账户中心（/account-api）两个前缀：短剧、
 * agent-platform 等下游一律由 admin 的 /ext/<svc>/* 反代，前端不直连业务后端。
 */

export const TOKEN_STORAGE_KEY = 'suite.jwt';
export const DEVICE_ID_STORAGE_KEY = 'suite.device.id';
const REFRESH_REJECTED_STORAGE_KEY = 'suite.admin.refresh.rejected';
let volatileDeviceId: string | null = null;

function newDeviceId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `device-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  );
}

export function getAccountDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing) return existing;
    const generated = newDeviceId();
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, generated);
    return generated;
  } catch {
    volatileDeviceId ??= newDeviceId();
    return volatileDeviceId;
  }
}

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    sessionStorage.removeItem(REFRESH_REJECTED_STORAGE_KEY);
  } catch {
    /* ignore quota / disabled storage */
  }
}

export function clearToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
  } catch {
    /* ignore unavailable session storage */
  }
}

/** 各后端前缀（与 nginx location 对齐）。 */
export const API_PREFIX = {
  admin: '/admin-api',
  // account-service OIDC IdP（自研统一身份）：/account-api/login、/token、/.well-known/*。
  // nginx 剥 /account-api/ 前缀 → account-service 根。
  account: '/account-api',
} as const;

interface ClientOpts {
  /** 发送 cookie（admin-core 用 session cookie 鉴权）。 */
  withCredentials?: boolean;
  /** 401 时是否尝试账户中心 refresh token 旋转续期。 */
  refreshOn401?: boolean;
  /** refresh 失败后是否清登录态并跳 /login。 */
  redirectOn401?: boolean;
}

interface RefreshRaw {
  access_token?: string;
}

let refreshInFlight: Promise<string | null> | null = null;
const REFRESH_LOCK_NAME = 'suite-admin-account-token-refresh';

export async function refreshAccountToken(): Promise<string | null> {
  try {
    if (sessionStorage.getItem(REFRESH_REJECTED_STORAGE_KEY) === '1') return null;
  } catch {
    /* unavailable storage: rely on the HttpOnly cookie and server response */
  }
  if (!refreshInFlight) {
    const requestRefresh = () =>
      axios
        .post<RefreshRaw>(
          `${API_PREFIX.account}/token`,
          {
            grant_type: 'refresh_token',
            client_id: 'admin-console',
          },
          {
            timeout: 30_000,
            headers: {
              'Content-Type': 'application/json',
              'X-Device-Id': getAccountDeviceId(),
            },
            withCredentials: true,
          },
        )
        .then((res) => {
          const access = res.data?.access_token ?? '';
          if (!access) return null;
          setToken(access);
          return access;
        })
        .catch((error) => {
          const status = axios.isAxiosError(error) ? error.response?.status : undefined;
          if (status === 400 || status === 401) {
            try {
              sessionStorage.setItem(REFRESH_REJECTED_STORAGE_KEY, '1');
            } catch {
              /* ignore unavailable storage */
            }
          }
          if (status === 400 || status === 401) return null;
          // A transport failure is not evidence that the session was revoked.
          throw error;
        });
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    // admin refresh token 是共享的 HttpOnly Cookie，而 access token 是标签页隔离的；
    // 串行后每个标签页都用最新 Cookie 各自换取 access token。
    const refresh = locks
      ? locks
          .request<Promise<string | null>>(REFRESH_LOCK_NAME, requestRefresh)
          .then((access) => access)
      : requestRefresh();
    refreshInFlight = refresh.finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

export async function restoreEmergencyAdminSession(): Promise<boolean> {
  try {
    await axios.get(`${API_PREFIX.admin}/auth/me`, {
      timeout: 30_000,
      withCredentials: true,
    });
    return true;
  } catch {
    return false;
  }
}

function createClient(prefix: string, opts: ClientOpts = {}): AxiosInstance {
  const refreshOn401 = opts.refreshOn401 ?? true;
  const redirectOn401 = opts.redirectOn401 ?? true;
  const instance = axios.create({
    baseURL: prefix,
    timeout: 30_000,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: opts.withCredentials ?? false,
  });

  instance.interceptors.request.use((config) => {
    const token = getToken();
    config.headers = config.headers ?? {};
    // 仅在调用方未显式带 Authorization 时注入用户 JWT。
    // 例外:模型列表/调用需用 sk- key(`Authorization: Bearer sk-...`),
    // 不能被用户 JWT 覆盖——否则 /v1/* 校验失败 401 触发跳登录。
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (prefix === API_PREFIX.account && !config.headers['X-Device-Id']) {
      config.headers['X-Device-Id'] = getAccountDeviceId();
    }
    return config;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error?.response?.status;
      const config = error?.config as
        | (AxiosRequestConfig & { __accountRefreshRetried?: boolean })
        | undefined;
      if (status === 401) {
        if (refreshOn401 && config && !config.__accountRefreshRetried) {
          config.__accountRefreshRetried = true;
          // A slower request can return 401 after another request already refreshed.
          const current = getToken();
          const access =
            current && config.headers?.Authorization !== `Bearer ${current}`
              ? current
              : await refreshAccountToken();
          if (access) {
            config.headers = { ...(config.headers ?? {}), Authorization: `Bearer ${access}` };
            return instance.request(config);
          }
        }
        if (redirectOn401) {
          clearToken();
          if (!window.location.pathname.endsWith('/login')) {
            const base = import.meta.env.BASE_URL || '/';
            window.location.assign(`${base.replace(/\/$/, '')}/login`);
          }
        }
      }
      return Promise.reject(error);
    },
  );

  return instance;
}

/** admin（/admin-api/*）：账户中心 JWT 经通用拦截器注入；敏感操作走 step-up。 */
export const adminApi = createClient(API_PREFIX.admin, {
  withCredentials: true,
});

// step-up:请求带上内存里的令牌;响应撞到 STEP_UP_REQUIRED 时弹 TOTP 后自动重试。
adminApi.interceptors.request.use((config) => {
  const su = getStepUpToken();
  if (su) {
    config.headers = config.headers ?? {};
    if (!config.headers['X-Step-Up']) config.headers['X-Step-Up'] = su;
  }
  return config;
});
adminApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status;
    const details = error?.response?.data?.error?.details as
      | { step_up_required?: boolean }
      | undefined;
    const cfg = error?.config as (AxiosRequestConfig & { __stepUpRetried?: boolean }) | undefined;
    if (status === 403 && details?.step_up_required && cfg && !cfg.__stepUpRetried) {
      const prompt = getStepUpPrompt();
      if (!prompt) return Promise.reject(error);
      const token = await prompt(); // 模态完成 TOTP(必要时先绑 MFA)→ step-up 令牌
      if (!token) return Promise.reject(error);
      setStepUpToken(token);
      cfg.__stepUpRetried = true;
      cfg.headers = { ...(cfg.headers ?? {}), 'X-Step-Up': token };
      return adminApi.request(cfg);
    }
    return Promise.reject(error);
  },
);
/** account-service OIDC IdP（/account-api/*）：自研登录 /login + /token（PKCE）。登录页专用。 */
export const accountApi = createClient(API_PREFIX.account, {
  refreshOn401: false,
  redirectOn401: false,
});

/** 便捷 request 包装（返回 data）。 */
export async function request<T>(client: AxiosInstance, config: AxiosRequestConfig): Promise<T> {
  const res = await client.request<T>(config);
  return res.data;
}
